/* eslint-disable jsx-a11y/anchor-is-valid */
import {useEffect, useState} from 'react'
import {createPortal} from 'react-dom'
import {Typeahead} from 'react-bootstrap-typeahead'
import {useNavigate, Link, useMatch} from 'react-router-dom'
import 'react-bootstrap-typeahead/css/Typeahead.css'

import {useSearchContext} from './SearchContext'
import {useViewImageContext} from '../Fields/ViewField/ViewLand/Images/ViewImageContext'
import {useMapLegendContext} from '../Fields/ViewField/ViewLand/Helpers/MapLegend'
import {buildLandViewPath, useViewLandContext} from '../Fields/ViewField/ViewLand/ViewLandContext'
import {createSelectedOptionAwareFilter} from '../services/typeaheadFilters'
import {useTranslation} from 'react-i18next'
import {ChatGptLogo, getCopilotLayerImageType, SpecificCopilot} from '../Copilot'
import {COPILOT_ENABLED} from '../../../config/featureFlags'

const searchFilterBy = createSelectedOptionAwareFilter('label')

export const Search = () => {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const {options} = useSearchContext()

  return (
    <div className='gis-toolbar-search'>
      <Typeahead
        id='search_field'
        labelKey='label'
        filterBy={searchFilterBy}
        maxResults={200000}
        options={options}
        onChange={(e) => {
          if (e.length === 1) {
            const [land] = e
            navigate(buildLandViewPath(land.field_id, land.id))
          }
        }}
        placeholder={t('common:toolbar.search_placeholder')}
      />
    </div>
  )
}

export const CreateFieldButton = ({className = ''}) => {
  const navigate = useNavigate()
  const {t} = useTranslation()
  let isCreatingLand = useMatch('/gis/field/view/:field_id/land/new')
  let isCreatingGap = useMatch('/gis/field/view/:field_id/gap/new')
  const isCreatingField = useMatch('/gis/field/new')
  isCreatingLand = isCreatingLand || isCreatingGap || isCreatingField

  if (isCreatingField) {
    return <></>
  }

  return (
    <button
      disabled={isCreatingLand}
      className={`btn btn-sm btn-primary cursor-pointer gis-toolbar-action ${className}`.trim()}
      id='kt_toolbar_primary_button'
      onClick={() => navigate('/gis/field/new')}
    >
      {t('fields:buttons.create_field')}
    </button>
  )
}

export const Reports = ({className = ''}) => {
  const {t} = useTranslation()
  return (
    <Link
      to='/gis/reports'
      className={`btn btn-sm btn-light-primary cursor-pointer gis-toolbar-action ${className}`.trim()}
    >
      {t('common:toolbar.buttons.reports')}
    </Link>
  )
}

export const ListFieldButton = ({className = ''}) => {
  const {t} = useTranslation()
  return (
    <Link
      id='gis-toolbar-fields-link'
      to='/gis/field/list'
      className={`btn btn-sm btn-light-primary cursor-pointer gis-toolbar-action ${className}`.trim()}
    >
      {t('common:toolbar.buttons.fields')}
    </Link>
  )
}

export const RemoveImages = ({className = '', showDivider = true}) => {
  const imageContext = useViewImageContext()
  const mapLegendContext = useMapLegendContext()
  const {t} = useTranslation()
  const isLandRoute = useMatch('/gis/field/view/:field_id/land/view/:land_id/*')

  if (!isLandRoute || !imageContext.hasCurrentLandMapOverlay) {
    return <></>
  }

  return (
    <>
      <button
        onClick={() => {
          mapLegendContext.deleteAll()
          imageContext.closeAllImages()
        }}
        className={`btn btn-sm btn-light-danger cursor-pointer gis-toolbar-action ${className}`.trim()}
      >
        {t('common:toolbar.buttons.remove_images')}
      </button>
      {showDivider && <div className='gis-toolbar-divider'></div>}
    </>
  )
}

const useMapAiControl = (enabled) => {
  const [controlElement, setControlElement] = useState(null)

  useEffect(() => {
    if (!enabled) {
      setControlElement(null)
      return undefined
    }

    let control = null
    let container = null

    const mountControl = () => {
      const nextContainer = document.querySelector('.mapboxgl-ctrl-top-left')
      const geocoder = nextContainer?.querySelector('.mapboxgl-ctrl-geocoder')
      if (!nextContainer || !geocoder) {
        return
      }

      if (container === nextContainer && control?.isConnected) {
        if (control.previousElementSibling !== geocoder) {
          geocoder.insertAdjacentElement('afterend', control)
        }
        return
      }

      control?.remove()
      container = nextContainer
      control = document.createElement('div')
      control.className = 'mapboxgl-ctrl gis-map-ai-control'
      control.dataset.mapControl = 'ai-analysis'
      geocoder.insertAdjacentElement('afterend', control)
      setControlElement(control)
    }

    mountControl()
    const observer = new MutationObserver(mountControl)
    observer.observe(document.body, {childList: true, subtree: true})

    return () => {
      observer.disconnect()
      control?.remove()
    }
  }, [enabled])

  return controlElement
}

const LandAiAnalysis = () => {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const viewLandContext = useViewLandContext()
  const viewImageContext = useViewImageContext()
  const landMatch = useMatch('/gis/field/view/:field_id/land/view/:land_id/*')
  const [open, setOpen] = useState(false)
  const [pendingImageType, setPendingImageType] = useState(null)
  const landId = landMatch?.params?.land_id
  const controlElement = useMapAiControl(COPILOT_ENABLED && Boolean(landId))

  useEffect(() => {
    const land = viewLandContext.land

    if (!pendingImageType || !land?.land_id || viewImageContext.loading) {
      return
    }

    viewImageContext.changeImageType(land, pendingImageType)
    setPendingImageType(null)
  }, [pendingImageType, viewImageContext, viewLandContext.land])

  if (!COPILOT_ENABLED || !landId) {
    return null
  }

  const handleAction = (action) => {
    if (action?.type === 'open_report' && action.reportId) {
      setOpen(false)
      navigate(`/gis/reports/economic/view/${action.reportId}`)
      return
    }

    const targetImageType = getCopilotLayerImageType(action)

    if (targetImageType) {
      setOpen(false)
      setPendingImageType(targetImageType)
      viewLandContext.navigateToSection('images')
      return
    }

    if (action?.type === 'open_historical_ambient') {
      setOpen(false)
      viewLandContext.navigateToSection('ambients')
    }
  }

  return (
    <>
      {controlElement &&
        createPortal(
          <button
            type='button'
            className='btn btn-sm btn-primary gis-map-ai-action'
            onClick={() => setOpen(true)}
            aria-label={t('copilot:button')}
            title={t('copilot:button')}
          >
            <ChatGptLogo className='gis-map-ai-logo' />
            <span>{t('copilot:button')}</span>
          </button>,
          controlElement
        )}
      <SpecificCopilot open={open} setOpen={setOpen} landId={landId} onAction={handleAction} />
    </>
  )
}

export const Content = () => {
  return (
    <div className='gis-toolbar-actions'>
      <RemoveImages />
      <Search />
      <div className='gis-toolbar-divider'></div>
      <Reports />
      <ListFieldButton />
      <LandAiAnalysis />
    </div>
  )
}

export const Toolbar = () => {
  return (
    <div className='toolbar gis-toolbar' id='kt_toolbar'>
      <div id='kt_toolbar_container' className='container-fluid gis-toolbar-shell'>
        <div className='gis-toolbar-floating'>
          <Content />
        </div>
      </div>
    </div>
  )
}
